import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Info,
  ArrowRight,
  Car as CarIcon,
} from "lucide-react";
import { resolveImageUrl } from "@/lib/api";
import { fmt, fromKm } from "@/lib/units";
import { fmtMoney } from "@/lib/currency";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * VehicleOverviewCard — top-of-Dashboard snapshot that morphs based on the
 * current vehicle filter:
 *
 *   • Single vehicle (or fleet of 1 active)  → "Vehicle Snapshot"
 *       — vehicle image + 4 vehicle-specific metrics
 *   • 2–3 active vehicles, "All" filter      → "Fleet Snapshot"
 *       — stacked thumbnails + aggregate metrics
 *   • 4+ active vehicles, "All" filter        → "Portfolio Overview"
 *       — no images, 5 portfolio metrics
 *
 * Data source: the backend `/api/stats` response (passed in via the `stats`
 * prop). All cost / efficiency / business-share values are read straight
 * from that payload — the card never recomputes from raw trips / energy
 * events. This keeps Vehicle Overview and KPI Cards in lock-step.
 *
 * Distance ("This Month") reads `stats.month_used_km`, which is genuinely
 * month-scoped on the backend. All other metrics reflect the active
 * date-filter range (identical to KPI Cards).
 *
 * Sits ABOVE SmartSummaryBanner. Does not touch any existing layout.
 */

const VARIANT = {
  SINGLE: "single",
  FLEET: "fleet",
  PORTFOLIO: "portfolio",
};

function detectVariant(selectedVehicleId, activeVehicles) {
  if (selectedVehicleId && selectedVehicleId !== "all") return VARIANT.SINGLE;
  const n = activeVehicles?.length || 0;
  if (n <= 1) return VARIANT.SINGLE;
  if (n <= 3) return VARIANT.FLEET;
  return VARIANT.PORTFOLIO;
}

function VehicleThumb({ vehicle, size = "md" }) {
  const src = resolveImageUrl(vehicle?.image_url);
  const dim =
    size === "sm" ? "w-12 h-12" : size === "md" ? "w-16 h-16 sm:w-20 sm:h-20" : "w-24 h-24 sm:w-28 sm:h-28";
  return (
    <div
      className={`relative shrink-0 ${dim} bg-[var(--bg-emphasis)] border border-[var(--border-subtle)] flex items-center justify-center overflow-hidden`}
      style={{ borderRadius: 4 }}
      aria-hidden="true"
    >
      {src ? (
        <img
          src={src}
          alt={vehicle?.name || "Vehicle"}
          className="w-full h-full object-cover"
        />
      ) : (
        <CarIcon className="w-6 h-6 text-[var(--text-faint)]" strokeWidth={1.5} />
      )}
    </div>
  );
}

function Metric({ value, sub, sub2, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="font-display text-xl sm:text-2xl lg:text-[26px] font-light tracking-tighter text-[var(--text-primary)] leading-none whitespace-nowrap">
        {value}
      </div>
      {sub && (
        <div className="text-[11px] text-[var(--text-secondary)] mt-1.5 leading-snug">{sub}</div>
      )}
      {sub2 && (
        <div className="text-[10px] text-[var(--text-muted)] leading-snug">{sub2}</div>
      )}
    </div>
  );
}

function CardHeader({ title, tooltip }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="label-tiny">{title}</span>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="What this card shows"
              className="text-[var(--text-faint)] hover:text-[var(--text-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E82127]/60"
            >
              <Info className="w-3 h-3" strokeWidth={1.75} />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="bg-[var(--bg-input)] border border-[var(--border-subtle)] text-[var(--text-primary)] text-xs max-w-[260px]"
          >
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

function FooterStatus() {
  return (
    <div className="mt-auto pt-3 border-t border-[var(--border-hover)] flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
        <CheckCircle2 className="w-3.5 h-3.5 text-[#34D17A] shrink-0" strokeWidth={1.75} />
        <span>All key metrics are within expected range.</span>
      </div>
      <Link
        to="/vehicles"
        className="inline-flex items-center gap-1 text-xs text-[#E82127] hover:text-[#FF2A30] tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E82127]/60 rounded-sm"
        data-testid="vehicle-overview-details-link"
      >
        View Vehicles
        <ArrowRight className="w-3 h-3" strokeWidth={1.75} />
      </Link>
    </div>
  );
}

export default function VehicleOverviewCard({
  vehicles = [],
  selectedVehicleId = "all",
  trips = [],
  energyEvents = [],
  stats = null,
  projection = null,
  unit = "km",
  currencySymbol = "$",
}) {
  const activeVehicles = useMemo(
    () => (vehicles || []).filter((v) => !v.archived),
    [vehicles],
  );

  const variant = useMemo(
    () => detectVariant(selectedVehicleId, activeVehicles),
    [selectedVehicleId, activeVehicles],
  );

  // Vehicle resolver for single mode — favour explicit filter, else the one
  // active vehicle, else null (no card shown).
  const focusVehicle = useMemo(() => {
    if (selectedVehicleId && selectedVehicleId !== "all") {
      return activeVehicles.find((v) => v.id === selectedVehicleId) || vehicles.find((v) => v.id === selectedVehicleId) || null;
    }
    if (activeVehicles.length === 1) return activeVehicles[0];
    return null;
  }, [selectedVehicleId, activeVehicles, vehicles]);

  // ---- Single source of truth: backend `/api/stats` response. -----------
  // Vehicle Overview consumes the same numbers KPI Cards consume so the two
  // cards never diverge. No per-event totals are recomputed here.
  // stats.month_used_km IS month-scoped server-side; everything else under
  // stats.energy / stats.efficiency reflects the active date-filter range
  // (identical to what KPI Cards render).
  const metrics = useMemo(() => {
    const e = stats?.energy || {};
    const eff = stats?.efficiency || {};
    const monthKm = Number(stats?.month_used_km || 0);
    const chargeCost = Number(e.charge_cost || 0);
    const fuelCost = Number(e.fuel_cost || 0);
    const totalCost = Number(e.total_cost || 0);
    const costPerKm =
      e.cost_per_km != null && Number.isFinite(Number(e.cost_per_km))
        ? Number(e.cost_per_km)
        : null;
    const kmPerKwh =
      eff.avg_km_per_kwh != null && Number(eff.avg_km_per_kwh) > 0
        ? Number(eff.avg_km_per_kwh)
        : null;
    const kwhPer100Km = kmPerKwh ? 100 / kmPerKwh : null;
    const lPer100Km =
      eff.avg_l_per_100km != null && Number(eff.avg_l_per_100km) > 0
        ? Number(eff.avg_l_per_100km)
        : null;
    const businessKm = Number(stats?.business_km || 0);
    const personalKm = Number(stats?.personal_km || 0);
    const usageKm = businessKm + personalKm;
    const businessPct = usageKm > 0 ? Math.round((businessKm / usageKm) * 100) : 0;
    // Projected annual cost = backend cost/km × projected year-end distance.
    // Both inputs come from the backend; no per-event math is reproduced here.
    const projectedKm = Number(projection?.km || 0);
    const projectedAnnualCost =
      costPerKm != null && projectedKm > 0 ? costPerKm * projectedKm : 0;

    return {
      monthKm,
      chargeCost,
      fuelCost,
      totalCost,
      costPerKm,
      kwhPer100Km,
      lPer100Km,
      businessPct,
      projectedAnnualCost,
    };
  }, [stats, projection]);

  // Don't render the card while data is still warming up — avoids flashing.
  const noVehicles = !vehicles || vehicles.length === 0;

  if (noVehicles) return null;

  const u = unit === "mi" ? "mi" : "km";
  const displayKm = (km) => fmt(fromKm(km || 0, unit), 0);
  // Currency helpers — pinned to spec (2 dp for totals, 3 dp for cost/km).
  const money2 = (v) =>
    v == null || !Number.isFinite(Number(v))
      ? `${currencySymbol}—`
      : fmtMoney(Number(v), currencySymbol, 2);
  const money3 = (v) =>
    v == null || !Number.isFinite(Number(v))
      ? "—"
      : fmtMoney(Number(v), currencySymbol, 3);

  // -------------- Render: SINGLE --------------
  if (variant === VARIANT.SINGLE) {
    const v = focusVehicle;
    if (!v) return null;
    const isFuel = v.energy_type === "gas";
    const isHybrid = v.energy_type === "hybrid";
    const effValue = isFuel
      ? metrics.lPer100Km != null
        ? metrics.lPer100Km.toFixed(1)
        : "—"
      : metrics.kwhPer100Km != null
        ? metrics.kwhPer100Km.toFixed(1)
        : isHybrid && metrics.lPer100Km != null
          ? metrics.lPer100Km.toFixed(1)
          : "—";
    const effSubLabel = isFuel
      ? "L/100km"
      : isHybrid && metrics.kwhPer100Km == null && metrics.lPer100Km != null
        ? "L/100km"
        : "kWh/100km";

    const costLabel = isFuel ? "Fuel Cost" : "Charging Cost";
    const costValue = isFuel ? metrics.fuelCost : metrics.chargeCost;

    return (
      <div
        className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] p-4 lg:p-5 h-full flex flex-col"
        style={{ borderRadius: 4 }}
        data-testid="vehicle-overview-card"
        data-variant="single"
      >
        <CardHeader
          title="Vehicle Snapshot"
          tooltip="Key metrics for this vehicle for the current calendar month."
        />
        <div className="mt-3 flex flex-col sm:flex-row gap-4 lg:gap-6 items-start">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <VehicleThumb vehicle={v} size="lg" />
            <div className="min-w-0 sm:hidden">
              <div className="font-display text-sm tracking-tight text-[var(--text-primary)] truncate">{v.name}</div>
              <div className="text-[10px] tracking-wider uppercase text-[var(--text-faint)]">{v.energy_type || "EV"}</div>
            </div>
          </div>
          <div className="hidden sm:block">
            <div className="font-display text-base tracking-tight text-[var(--text-primary)] truncate">{v.name}</div>
            <div className="text-[10px] tracking-wider uppercase text-[var(--text-faint)]">{v.energy_type || "EV"}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-3 flex-1 w-full">
            <Metric
              value={
                <>
                  {displayKm(metrics.monthKm)}
                  <span className="text-sm text-[var(--text-muted)] ml-1 font-mono-data">{u}</span>
                </>
              }
              sub="Driven"
              sub2="This Month"
              testid="vsnap-driven"
            />
            <Metric
              value={money2(costValue)}
              sub={costLabel}
              testid="vsnap-cost"
            />
            <Metric
              value={
                <>
                  {effValue}
                  <span className="text-sm text-[var(--text-muted)] ml-1 font-mono-data">{effSubLabel}</span>
                </>
              }
              sub="Avg. Efficiency"
              testid="vsnap-efficiency"
            />
            <Metric
              value={money3(metrics.costPerKm)}
              sub="Cost / km"
              testid="vsnap-cost-per-km"
            />
          </div>
        </div>
        <FooterStatus />
      </div>
    );
  }

  // -------------- Render: FLEET --------------
  if (variant === VARIANT.FLEET) {
    const thumbs = activeVehicles.slice(0, 3);
    return (
      <div
        className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] p-4 lg:p-5 h-full flex flex-col"
        style={{ borderRadius: 4 }}
        data-testid="vehicle-overview-card"
        data-variant="fleet"
      >
        <CardHeader
          title="Fleet Snapshot"
          tooltip="Aggregate metrics across all your active vehicles for the current calendar month."
        />
        <div className="mt-3 flex flex-col sm:flex-row gap-4 lg:gap-6 items-start">
          <div className="flex items-center -space-x-3">
            {thumbs.map((v) => (
              <div key={v.id} className="ring-2 ring-[var(--bg-elevated)]" style={{ borderRadius: 4 }}>
                <VehicleThumb vehicle={v} size="md" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-5 gap-y-3 flex-1 w-full">
            <Metric
              value={
                <>
                  {displayKm(metrics.monthKm)}
                  <span className="text-sm text-[var(--text-muted)] ml-1 font-mono-data">{u}</span>
                </>
              }
              sub="Total Driven"
              sub2="This Month"
              testid="vsnap-fleet-driven"
            />
            <Metric
              value={money2(metrics.totalCost)}
              sub="Total Spend"
              testid="vsnap-fleet-spend"
            />
            <Metric
              value={money3(metrics.costPerKm)}
              sub="Avg. Cost / km"
              testid="vsnap-fleet-cost-per-km"
            />
            <Metric
              value={activeVehicles.length}
              sub="Vehicles"
              sub2="Selected"
              testid="vsnap-fleet-count"
            />
          </div>
        </div>
        <FooterStatus />
      </div>
    );
  }

  // -------------- Render: PORTFOLIO --------------

  return (
    <div
      className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] p-4 lg:p-5 h-full flex flex-col"
      style={{ borderRadius: 4 }}
      data-testid="vehicle-overview-card"
      data-variant="portfolio"
    >
      <CardHeader
        title="Portfolio Overview"
        tooltip="Fleet-wide snapshot for the current calendar month plus a projected annual cost based on this month's pace."
      />
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-x-5 gap-y-3">
        <Metric value={activeVehicles.length} sub="Vehicles" sub2="Active" testid="vsnap-port-vehicles" />
        <Metric
          value={
            <>
              {displayKm(metrics.monthKm)}
              <span className="text-sm text-[var(--text-muted)] ml-1 font-mono-data">{u}</span>
            </>
          }
          sub="Total Distance"
          sub2="This Month"
          testid="vsnap-port-distance"
        />
        <Metric
          value={money2(metrics.totalCost)}
          sub="Total Spend"
          testid="vsnap-port-spend"
        />
        <Metric
          value={`${metrics.businessPct}%`}
          sub="Business Usage"
          testid="vsnap-port-business"
        />
        <Metric
          value={money2(metrics.projectedAnnualCost)}
          sub="Projected Annual"
          sub2="Cost"
          testid="vsnap-port-annual"
        />
      </div>
      <FooterStatus />
    </div>
  );
}
